import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { flexRender } from '@tanstack/react-table';
// react-table 9 moved to a feature-based API and parked the v8 surface behind
// `/legacy`. Staying on the legacy entry keeps this table (and its five
// callers) working unchanged; porting to the feature API is its own job, not
// a dependency bump's.
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useLegacyTable, } from '@tanstack/react-table/legacy';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Loader2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
export const DataTable = ({ data, loading, columns, rowSelection, sorting, columnFilters, columnVisibility, onRowClick, onRowSelectionChange, onSortingChange, onColumnFiltersChange, onColumnVisibilityChange, }) => {
    const { t } = useTranslation();
    const table = useLegacyTable({
        data,
        columns,
        onSortingChange,
        onColumnFiltersChange,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: rowSelection ? getFilteredRowModel() : undefined,
        onColumnVisibilityChange,
        onRowSelectionChange,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    });
    return (<div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (<TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                return (<TableHead className="text-center" key={header.id}>
                                            {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>);
            })}
                            </TableRow>))}
                    </TableHeader>
                    <TableBody>
                        {loading ? (<TableRow>
                                <TableCell colSpan={columns.length} className="h-24">
                                    <div className="flex items-center justify-center space-x-2">
                                        <Loader2Icon className="animate-spin mr-2"/>
                                        {t('loading', { ns: 'table' })}
                                    </div>
                                </TableCell>
                            </TableRow>) : table.getRowModel().rows?.length ? (table.getRowModel().rows.map((row) => (<TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} onClick={() => onRowClick?.(row.original)}>
                                    {row.getVisibleCells().map((cell) => (<TableCell key={cell.id} className={cn('text-center', cell.column.columnDef.meta?.className)}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>))}
                                </TableRow>))) : (<TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    {t('no_data', { ns: 'table' })}
                                </TableCell>
                            </TableRow>)}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 p-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length}&nbsp;{t('of', { ns: 'table' })}&nbsp;
                    {table.getFilteredRowModel().rows.length}
                    &nbsp;
                    {t('row_selected', { ns: 'table' })}
                </div>
                <div className="space-x-2">
                    <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                        {t('previous', { ns: 'table' })}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                        {t('next', { ns: 'table' })}
                    </Button>
                </div>
            </div>
        </div>);
};
DataTable.displayName = 'DataTable';
